import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { templateFiltersAtom, templateListRefreshAtom } from '../state/templateAtoms';
import { templateService } from '../services/templateService';
import { applyTemplateOrganisationScope } from '../utils/templateFilterUtils';

const EMPTY_COUNTS = { total: 0, approved: 0, pending: 0, rejected: 0, archived: 0 };

export default function useTemplateListCounts() {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const filters = useRecoilValue(templateFiltersAtom);
  const listRefresh = useRecoilValue(templateListRefreshAtom);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const cacheRef = useRef(EMPTY_COUNTS);

  const fetchCounts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const scoped = applyTemplateOrganisationScope(filters, loggedInUser);
      const result = await templateService.getTemplateListCounts({
        companyId: scoped.organisation || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      const next = result ?? EMPTY_COUNTS;
      cacheRef.current = next;
      setCounts(next);
    } catch (err) {
      setError(err?.message || 'Failed to load template counts');
      setCounts(cacheRef.current);
    } finally {
      setIsLoading(false);
    }
  }, [filters.endDate, filters.organisation, filters.startDate, loggedInUser]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, listRefresh]);

  return { counts, isLoading, error, refetch: fetchCounts };
}
